import { useQuery } from "@tanstack/react-query";
import type { LinkPreview as ThetaLinkPreview } from "@theta/api-client";
import { isThetaError } from "@theta/api-client";
import { thetaApi } from "@/lib/api/theta";

type ResolvedPreview =
  | { kind: "preview"; preview: ThetaLinkPreview }
  | { kind: "none" };

/**
 * One preview for one URL, from the API.
 *
 * INTERNAL AND EXTERNAL BOTH COME FROM THE SERVER NOW, for two different
 * reasons. An internal link reveals a group name, a post excerpt and an author,
 * and whether this reader may have those depends on who they are — so it is
 * resolved under their session. An external link needs no session, but it lives
 * server-side anyway because Expo has no Next.js route to call; that is exactly
 * what spec §2.9 asked for, and it is why `app/api/og/route.ts` is gone.
 *
 * A Theta link the reader may not see comes back as `notFound`, which resolves
 * to `{kind: "none"}` — the caller renders a plain link and says nothing about
 * why. Same answer as a deleted post, on purpose: the reader must not be able
 * to tell "you can't see this" from "it isn't there".
 */
export function useResolvedLinkPreview(url: string | null, enabled = true) {
  return useQuery<ResolvedPreview>({
    enabled: enabled && Boolean(url),
    queryKey: ["resolved-link-preview", url],
    staleTime: 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const preview = await thetaApi.linkPreview(url!);
        return preview ? { kind: "preview", preview } : { kind: "none" };
      } catch (err) {
        // notFound covers BOTH "not visible to you" and "gone". Treat them
        // identically — distinguishing them here would leak the difference the
        // server went to the trouble of hiding.
        if (isThetaError(err, "notFound")) return { kind: "none" };
        throw err;
      }
    },
  });
}
